import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    searchUsers,
    getFriends,
    getPendingSentRequests,
    getPendingReceivedRequests,
    sendFriendRequest,
    respondFriendRequest,
    deleteFriend,
    blockFriend,
    deleteFriendRequest,
} from '../api/social';
import { getAvatar } from '../api/avatars';
import Avatar from '../components/Avatar';
import NotificationBell from '../components/NotificationBell';
import { MoreIcon, HotPotatoIcon } from '../assets/icons';
import useToast from '../hooks/useToast';
import { fetchDefaultAvatarSvg } from '../utils';
import '../styles/FriendsPage.css';

const byUsername = (a, b) =>
    (a.username || '').localeCompare(b.username || '', undefined, { sensitivity: 'base' });

const displayName = (person) => person.username || 'Unknown';

const FriendsPage = () => {
    const history = useHistory();
    const [activeTab, setActiveTab] = useState('friends');
    const [friends, setFriends] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [receivedRequests, setReceivedRequests] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(true);
    const [avatars, setAvatars] = useState({});
    const [defaultAvatar, setDefaultAvatar] = useState('');
    const [openMenuId, setOpenMenuId] = useState(null);
    const [busyKey, setBusyKey] = useState('');
    const avatarCache = useRef({});
    const { error, successMessage, showSuccess, showError, clearMessages } = useToast();

    const fetchData = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            const [friendsData, sentData, receivedData] = await Promise.all([
                getFriends(),
                getPendingSentRequests(),
                getPendingReceivedRequests(),
            ]);
            setFriends(Array.isArray(friendsData) ? friendsData : []);
            setSentRequests(Array.isArray(sentData.sent_requests) ? sentData.sent_requests : []);
            setReceivedRequests(Array.isArray(receivedData.received_requests) ? receivedData.received_requests : []);
        } catch (err) {
            showError('Failed to load friends data.');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        let cancelled = false;
        fetchDefaultAvatarSvg()
            .then((svg) => {
                if (!cancelled) setDefaultAvatar(svg);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const loadAvatars = useCallback(async (ids) => {
        const missing = [...new Set(ids.filter(Boolean))].filter((id) => avatarCache.current[id] === undefined);
        if (missing.length === 0) return;

        const entries = await Promise.all(missing.map(async (id) => {
            try {
                const result = await getAvatar(id);
                return [id, result.avatar?.svg_data || ''];
            } catch (err) {
                console.error(`Failed to load avatar ${id}:`, err);
                return [id, ''];
            }
        }));

        const next = { ...avatarCache.current };
        entries.forEach(([id, svg]) => {
            next[id] = svg;
        });
        avatarCache.current = next;
        setAvatars(next);
    }, []);

    useEffect(() => {
        loadAvatars([
            ...friends.map((friend) => friend.avatar_id),
            ...sentRequests.map((req) => req.avatar_id),
            ...receivedRequests.map((req) => req.avatar_id),
            ...searchResults.map((user) => user.avatar_id),
        ]);
    }, [friends, sentRequests, receivedRequests, searchResults, loadAvatars]);

    useEffect(() => {
        const query = searchQuery.trim();
        if (query.length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            setHasSearched(false);
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setSearchLoading(true);
                const data = await searchUsers(query);
                if (!cancelled) {
                    setSearchResults(data.results || []);
                    setHasSearched(true);
                }
            } catch (err) {
                if (!cancelled) showError(err.message || 'Search failed.');
            } finally {
                if (!cancelled) setSearchLoading(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchQuery, showError]);

    useEffect(() => {
        if (!openMenuId) return undefined;
        const close = (event) => {
            if (!event.target.closest('.friends-menu') && !event.target.closest('.friends-menu-panel')) {
                setOpenMenuId(null);
            }
        };
        const onKey = (event) => {
            if (event.key === 'Escape') setOpenMenuId(null);
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [openMenuId]);

    const selectTab = (tab) => {
        setActiveTab(tab);
        setOpenMenuId(null);
        clearMessages();
    };

    const runAction = async (key, action, successText) => {
        try {
            setBusyKey(key);
            clearMessages();
            setOpenMenuId(null);
            await action();
            showSuccess(successText);
            window.dispatchEvent(new CustomEvent('friendRequestsUpdated'));
            await fetchData({ silent: true });
        } catch (err) {
            showError(err.message || 'Something went wrong.');
        } finally {
            setBusyKey('');
        }
    };

    const handleSendRequest = (recipId, username) => runAction(
        `send:${recipId}`,
        () => sendFriendRequest(recipId),
        `Friend request sent to ${username}.`,
    );

    const handleAcceptRequest = (senderId) => runAction(
        `accept:${senderId}`,
        () => respondFriendRequest(senderId, 'accepted'),
        'Friend request accepted.',
    );

    const handleRejectRequest = (senderId) => runAction(
        `reject:${senderId}`,
        () => respondFriendRequest(senderId, 'rejected'),
        'Friend request declined.',
    );

    const handleDeleteFriend = (friendId, username) => runAction(
        `remove:${friendId}`,
        () => deleteFriend(friendId),
        `${username} removed.`,
    );

    const handleBlockFriend = (friendId, username) => runAction(
        `block:${friendId}`,
        () => blockFriend(friendId),
        `${username} blocked.`,
    );

    const handleCancelRequest = (recipId, username) => runAction(
        `cancel:${recipId}`,
        () => deleteFriendRequest(recipId),
        `Request to ${username} cancelled.`,
    );

    const friendIds = useMemo(() => new Set(friends.map((friend) => friend.friend_id)), [friends]);
    const sentIds = useMemo(() => new Set(sentRequests.map((req) => req.friend_id)), [sentRequests]);
    const receivedIds = useMemo(() => new Set(receivedRequests.map((req) => req.friend_id)), [receivedRequests]);

    const query = searchQuery.trim();
    const queryKey = query.toLowerCase();
    const visibleFriends = useMemo(() => {
        const list = [...friends].sort(byUsername);
        if (!queryKey) return list;
        return list.filter((friend) => (friend.username || '').toLowerCase().includes(queryKey));
    }, [friends, queryKey]);

    const people = useMemo(() => {
        if (query.length < 2) return [];
        return [...searchResults]
            .filter((user) => !friendIds.has(user.auth_id))
            .sort(byUsername);
    }, [searchResults, friendIds, query]);

    const sortedReceived = useMemo(() => [...receivedRequests].sort(byUsername), [receivedRequests]);
    const sortedSent = useMemo(() => [...sentRequests].sort(byUsername), [sentRequests]);

    const avatarFor = (person) => {
        if (!person?.avatar_id) return defaultAvatar;
        const svg = avatars[person.avatar_id];
        if (svg) return svg;
        if (svg === '') return defaultAvatar;
        return '';
    };

    const openProfile = (person) => {
        const id = person.username || person.friend_id || person.auth_id;
        if (id) history.push(`/user/${id}`);
    };

    const renderAvatar = (person) => {
        const svg = avatarFor(person);
        const name = displayName(person);
        return (
            <span className="friends-avatar-ring">
                {svg ? (
                    <Avatar svgContent={svg} alt={`${name}'s avatar`} className="friends-avatar" />
                ) : (
                    <span className="friends-avatar friends-avatar--empty" aria-hidden="true" />
                )}
            </span>
        );
    };

    const renderPerson = (person, { subtitle, actions, menuId }) => {
        const name = displayName(person);
        const key = person.friend_id || person.auth_id || person.request_id;
        const menuOpen = menuId && openMenuId === menuId;
        return (
            <article key={key} className="friends-card">
                <button type="button" className="friends-card-main" onClick={() => openProfile(person)}>
                    {renderAvatar(person)}
                    <span className="friends-card-body">
                        <span className="friends-card-name">{name}</span>
                        {subtitle && <span className="friends-card-sub">{subtitle}</span>}
                    </span>
                </button>
                <div className="friends-card-actions">
                    {actions}
                    {menuId && (
                        <div className="friends-menu">
                            <button
                                type="button"
                                className="friends-menu-button"
                                aria-label={`Options for ${name}`}
                                aria-expanded={menuOpen}
                                onClick={() => setOpenMenuId(menuOpen ? null : menuId)}
                            >
                                <MoreIcon aria-hidden="true" />
                            </button>
                        </div>
                    )}
                </div>
                {menuOpen && (
                    <div className="friends-menu-panel" role="group" aria-label={`Options for ${name}`}>
                        <button
                            type="button"
                            className="friends-menu-item friends-menu-item--hot"
                            onClick={() => history.push(`/hot-potato/send?to=${encodeURIComponent(person.username || '')}`)}
                        >
                            <HotPotatoIcon aria-hidden="true" />
                            Send Hot Potato
                        </button>
                        <button
                            type="button"
                            className="friends-menu-item"
                            disabled={busyKey === `remove:${menuId}`}
                            onClick={() => handleDeleteFriend(menuId, name)}
                        >
                            Remove friend
                        </button>
                        <button
                            type="button"
                            className="friends-menu-item friends-menu-item--danger"
                            disabled={busyKey === `block:${menuId}`}
                            onClick={() => handleBlockFriend(menuId, name)}
                        >
                            Block
                        </button>
                    </div>
                )}
            </article>
        );
    };

    const renderSearchActions = (user) => {
        const id = user.auth_id;
        const name = displayName(user);
        if (sentIds.has(id)) {
            return <span className="friends-status friends-status--pending">Request sent</span>;
        }
        if (receivedIds.has(id)) {
            return (
                <button
                    type="button"
                    className="sc-btn sc-btn--primary sc-btn--sm"
                    disabled={busyKey === `accept:${id}`}
                    onClick={() => handleAcceptRequest(id)}
                >
                    Accept
                </button>
            );
        }
        return (
            <button
                type="button"
                className="sc-btn sc-btn--primary sc-btn--sm"
                disabled={busyKey === `send:${id}`}
                onClick={() => handleSendRequest(id, name)}
            >
                Add friend
            </button>
        );
    };

    const tabs = [
        { id: 'friends', label: 'Friends', count: friends.length },
        { id: 'received', label: 'Incoming', count: receivedRequests.length, alert: receivedRequests.length > 0 },
        { id: 'sent', label: 'Sent', count: sentRequests.length },
    ];

    return (
        <div className="friends-page">
            <div className="friends-container">
                <div className="friends-header">
                    <div>
                        <h2>Friends</h2>
                        <p className="friends-header-sub">Find people and manage requests</p>
                    </div>
                    <div className="friends-header-actions">
                        <NotificationBell />
                    </div>
                </div>

                {(error || successMessage) && (
                    <div className="friends-alerts">
                        {error && <div className="sc-alert sc-alert--error" role="alert">{error}</div>}
                        {successMessage && <div className="sc-alert sc-alert--success" role="status">{successMessage}</div>}
                    </div>
                )}

                {!loading && (
                    <div className="friends-stats" role="tablist" aria-label="Friend lists">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                className={[
                                    'friends-stat',
                                    activeTab === tab.id ? 'is-active' : '',
                                    tab.alert ? 'friends-stat--alert' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => selectTab(tab.id)}
                            >
                                <span className="friends-stat-value">{tab.count}</span>
                                <span className="friends-stat-label">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className="friends-content">
                    {loading ? (
                        <div className="friends-loading">
                            <div className="friends-spinner" />
                            <p>Loading friends...</p>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'friends' && (
                                <>
                                    <label className="friends-search">
                                        <span className="friends-search-label">Find people</span>
                                        <input
                                            type="search"
                                            value={searchQuery}
                                            onChange={(event) => setSearchQuery(event.target.value)}
                                            placeholder="Search by username"
                                            aria-label="Search by username"
                                        />
                                    </label>
                                    {query.length === 1 && (
                                        <p className="friends-hint">Type at least 2 characters to search everyone.</p>
                                    )}

                                    {query.length >= 2 && (
                                        <section className="friends-section">
                                            <p className="friends-section-title">People</p>
                                            {searchLoading && !hasSearched ? (
                                                <p className="friends-hint">Searching...</p>
                                            ) : people.length === 0 ? (
                                                <p className="friends-empty">
                                                    {searchLoading ? 'Searching...' : `No one new matches “${query}”.`}
                                                </p>
                                            ) : (
                                                <div className="friends-list">
                                                    {people.map((user) => renderPerson(user, {
                                                        subtitle: receivedIds.has(user.auth_id) ? 'Sent you a request' : null,
                                                        actions: renderSearchActions(user),
                                                    }))}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    <section className="friends-section">
                                        <p className="friends-section-title">
                                            {query ? 'Your friends' : 'All friends'}
                                        </p>
                                        {visibleFriends.length === 0 ? (
                                            <p className="friends-empty">
                                                {friends.length === 0
                                                    ? 'You don’t have any friends yet. Search for a username above to send a request.'
                                                    : `No friends match “${query}”.`}
                                            </p>
                                        ) : (
                                            <div className="friends-list">
                                                {visibleFriends.map((friend) => renderPerson(friend, {
                                                    subtitle: 'View profile',
                                                    menuId: friend.friend_id,
                                                }))}
                                            </div>
                                        )}
                                    </section>
                                </>
                            )}

                            {activeTab === 'received' && (
                                sortedReceived.length === 0 ? (
                                    <p className="friends-empty">No incoming requests.</p>
                                ) : (
                                    <div className="friends-list">
                                        {sortedReceived.map((req) => renderPerson(req, {
                                            subtitle: 'Sent you a request',
                                            actions: (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="sc-btn sc-btn--primary sc-btn--sm"
                                                        disabled={busyKey === `accept:${req.friend_id}`}
                                                        onClick={() => handleAcceptRequest(req.friend_id)}
                                                    >
                                                        Accept
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="sc-btn sc-btn--danger sc-btn--sm"
                                                        disabled={busyKey === `reject:${req.friend_id}`}
                                                        onClick={() => handleRejectRequest(req.friend_id)}
                                                    >
                                                        Decline
                                                    </button>
                                                </>
                                            ),
                                        }))}
                                    </div>
                                )
                            )}

                            {activeTab === 'sent' && (
                                sortedSent.length === 0 ? (
                                    <p className="friends-empty">You haven’t sent any requests.</p>
                                ) : (
                                    <div className="friends-list">
                                        {sortedSent.map((req) => renderPerson(req, {
                                            subtitle: 'Request pending',
                                            actions: (
                                                <button
                                                    type="button"
                                                    className="sc-btn sc-btn--secondary sc-btn--sm"
                                                    disabled={busyKey === `cancel:${req.friend_id}`}
                                                    onClick={() => handleCancelRequest(req.friend_id, displayName(req))}
                                                >
                                                    Cancel
                                                </button>
                                            ),
                                        }))}
                                    </div>
                                )
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FriendsPage;
